/**
 * CRM Authentication Client Utility
 * Manages CRM authentication tokens and provides fetch wrapper
 */

import { API_BASE } from '../config/api';

const CRM_TOKEN_KEY = 'crm_access_token';

/**
 * Get CRM access token from localStorage
 * @returns {string|null} Token or null if not found
 */
export const getCrmToken = () => {
  return localStorage.getItem(CRM_TOKEN_KEY);
};

/**
 * Set CRM access token in localStorage
 * @param {string} token - JWT access token
 */
export const setCrmToken = (token) => {
  localStorage.setItem(CRM_TOKEN_KEY, token);
};

/**
 * Clear CRM access token from localStorage
 */
export const clearCrmToken = () => {
  localStorage.removeItem(CRM_TOKEN_KEY);
};

/**
 * Fetch wrapper that automatically attaches CRM Authorization header
 * @param {string} url - API endpoint URL (relative or absolute)
 * @param {object} options - Fetch options (headers, method, body, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const crmFetch = async (url, options = {}) => {
  const token = getCrmToken();
  
  // Build full URL if relative
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
  
  // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  
  // Merge headers
  const headers = {
    ...options.headers,
  };
  
  // Only set Content-Type if not FormData and not already set
  if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Make request
  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });
  
  // Handle 401 - token invalid or expired
  if (response.status === 401) {
    clearCrmToken();
    // Don't throw here - let caller handle redirect
  }
  
  return response;
};
